import {
  startStream,
  stopStream,
  finishStream,
  isCurrentStream,
} from "@/utils/streamControllers";
import fetchData from "@/utils/fetchData";
import {
  streamList,
  StreamError,
  isRetryable,
  RETRY_LIMIT,
  retryDelay,
} from "@/utils/streamList";

const isQuestion = (item) =>
  item &&
  typeof item.question === "string" &&
  Array.isArray(item.suggestedAnswers);

export default {
  name: "questions",
  namespaced: true,
  state: {
    questionList: [],
    additionalQuestionList: [],
    contextQuestionList: [],
    loading: false,
    contextQuestionLoading: false,
    id: 0,
    activeEdit: "",
    chosenAnswers: [],
    isProcessingExtraQuestions: false,
  },
  mutations: {
    SET_QUESTION_LIST(state, payload) {
      state.questionList = payload;
    },
    APPEND_QUESTION_LIST(state, payload) {
      state.questionList.push(payload);
    },
    APPEND_QUESTION_LIST_FROM_ADDITIONAL_QUESTIONS(state) {
      state.questionList.push(state.additionalQuestionList.shift());
    },
    SET_ADDITIONAL_QUESTION_LIST(state, payload) {
      state.additionalQuestionList = payload;
    },
    APPEND_ADDITIONAL_QUESTION_LIST(state, payload) {
      state.additionalQuestionList.push(payload);
    },
    SET_CONTEXT_QUESTION_LIST(state, payload) {
      state.contextQuestionList = payload;
    },
    APPEND_CONTEXT_QUESTION_LIST(state, payload) {
      state.contextQuestionList.push(payload);
    },
    // Added answers go last, next to where they were typed
    APPEND_QUESTION_ANSWER(state, { question, value }) {
      question.suggestedAnswers.push(value);
      question.option = "";
    },
    SET_QUESTION_ANSWER(state, { question, value }) {
      question.suggestedAnswers = question.suggestedAnswers.map(
        (suggestedAnswer) =>
          suggestedAnswer === state.activeEdit ? value : suggestedAnswer
      );
    },
    SET_LOADING(state, payload) {
      state.loading = payload;
    },
    SET_CONTEXT_QUESTION_LOADING(state, payload) {
      state.contextQuestionLoading = payload;
    },
    INCREMENT_ID(state) {
      state.id += 1;
    },
    SET_ID(state, payload) {
      state.id = payload;
    },
    SET_ACTIVE_EDIT(state, payload) {
      state.activeEdit = payload;
    },
    SET_CHOSEN_ANSWERS(state, payload) {
      state.chosenAnswers = payload;
    },
    APPEND_CHOSEN_ANSWERS(state, payload) {
      state.chosenAnswers.push(payload);
    },
    SET_IS_PROCESSING_EXTRA_QUESTIONS(state, payload) {
      state.isProcessingExtraQuestions = payload;
    },
    MARK_FROM_CONTEXT(state, question) {
      question.fromContext = true;
    },
  },
  actions: {
    reset({ commit }) {
      stopStream("questions");
      stopStream("extraQuestions");
      stopStream("contextQuestions");
      stopStream("prefill");
      commit("SET_QUESTION_LIST", []);
      commit("SET_ADDITIONAL_QUESTION_LIST", []);
      commit("SET_CONTEXT_QUESTION_LIST", []);
      commit("SET_ID", 0);
      commit("SET_CHOSEN_ANSWERS", []);
      commit("SET_ACTIVE_EDIT", "");
      commit("SET_LOADING", false);
      commit("SET_CONTEXT_QUESTION_LOADING", false);
      commit("SET_IS_PROCESSING_EXTRA_QUESTIONS", false);
    },
    resetLoading({ commit, state, dispatch }) {
      if (state.loading) {
        commit("SET_LOADING", false);
        dispatch("getQuestions");
      }
      // A restored page may have been waiting on context questions
      if (state.contextQuestionLoading) {
        dispatch("addContextQuestions", { replaceQuestions: true });
      }
    },
    addQuestion({ commit, state }, { item, location }) {
      if (!isQuestion(item)) return;
      commit(location, { ...item, id: state.id, option: "" });
      commit("INCREMENT_ID");
    },
    waitForFinishLoading({ state }) {
      return new Promise((resolve) => {
        if (!state.loading) {
          return resolve();
        }
        const intervalId = setInterval(() => {
          if (!state.loading) {
            clearInterval(intervalId);
            resolve();
          }
        }, 100);
      });
    },
    replaceQuestionsUpTo8({ state, commit, dispatch, getters, rootState }) {
      const prompt = rootState.prompt;
      const startedAt = Date.now();
      return new Promise((resolve) => {
        const intervalId = setInterval(() => {
          // Give up if the prompt changed or the extra questions never come
          if (rootState.prompt !== prompt || Date.now() - startedAt > 60000) {
            clearInterval(intervalId);
            resolve();
            return;
          }
          if (
            state.additionalQuestionList.length > 0 &&
            state.questionList.length < 8
          ) {
            commit("APPEND_QUESTION_LIST_FROM_ADDITIONAL_QUESTIONS");
          }
          if (
            state.additionalQuestionList.length === 0 &&
            state.isProcessingExtraQuestions === false
          ) {
            dispatch("getExtraQuestions", {
              originalList: getters.getQuestionListAsString,
            });
          }
          if (state.questionList.length >= 8) {
            clearInterval(intervalId);
            resolve();
          }
        }, 100);
      });
    },
    async getQuestions({ dispatch, commit }, { retries = 0 } = {}) {
      if (retries === 0) {
        await dispatch("reset");
      }
      const controller = startStream("questions");
      commit("SET_QUESTION_LIST", []);
      commit("SET_LOADING", true);
      try {
        const { status, count } = await streamList(
          "generate/questions",
          {},
          {
            signal: controller.signal,
            onItem: (item) =>
              dispatch("addQuestion", {
                item,
                location: "APPEND_QUESTION_LIST",
              }),
          }
        );
        if (status === 401) {
          dispatch("modals/openAuth", null, { root: true });
        } else if (status >= 500 || (status === 200 && count === 0)) {
          throw new StreamError("No questions were generated");
        }
      } catch (error) {
        if (!isCurrentStream("questions", controller)) return;
        if (isRetryable(error) && retries < RETRY_LIMIT) {
          setTimeout(() => {
            if (isCurrentStream("questions", controller)) {
              dispatch("getQuestions", { retries: retries + 1 });
            }
          }, retryDelay(retries));
          return;
        }
        console.error(error);
      }
      if (isCurrentStream("questions", controller)) {
        finishStream("questions", controller);
        commit("SET_LOADING", false);
      }
    },
    async replaceQuestion(
      { dispatch, commit, state, getters },
      { questionId }
    ) {
      const originalList = getters.getQuestionListAsString;
      commit(
        "SET_QUESTION_LIST",
        state.questionList.filter((question) => question.id !== questionId)
      );
      commit(
        "SET_CHOSEN_ANSWERS",
        state.chosenAnswers.filter((item) => item.question.id !== questionId)
      );
      if (state.loading) return;
      if (state.additionalQuestionList.length > 0) {
        commit("APPEND_QUESTION_LIST_FROM_ADDITIONAL_QUESTIONS");
      } else {
        dispatch("getExtraQuestions", { originalList });
        await dispatch("replaceQuestionsUpTo8");
      }
    },
    async getExtraQuestions(
      { dispatch, commit, state, getters },
      { originalList, retries = 0 } = {}
    ) {
      if (retries === 0) {
        if (state.isProcessingExtraQuestions) return;
        commit("SET_IS_PROCESSING_EXTRA_QUESTIONS", true);
        await dispatch("waitForFinishLoading");
      }
      const controller = startStream("extraQuestions");
      commit("SET_LOADING", true);
      commit("SET_ID", Math.max(state.id, getters.getHighestId + 1));
      try {
        const { status, count } = await streamList(
          "generate/questions/additional",
          { original: originalList },
          {
            signal: controller.signal,
            onItem: (item) =>
              dispatch("addQuestion", {
                item,
                location: "APPEND_ADDITIONAL_QUESTION_LIST",
              }),
          }
        );
        if (status === 401) {
          dispatch("modals/openAuth", null, { root: true });
        } else if (status >= 500 || (status === 200 && count === 0)) {
          throw new StreamError("No extra questions were generated");
        }
      } catch (error) {
        if (!isCurrentStream("extraQuestions", controller)) return;
        if (isRetryable(error) && retries < RETRY_LIMIT) {
          setTimeout(() => {
            if (isCurrentStream("extraQuestions", controller)) {
              dispatch("getExtraQuestions", {
                originalList,
                retries: retries + 1,
              });
            }
          }, retryDelay(retries));
          return;
        }
        console.error(error);
      }
      if (isCurrentStream("extraQuestions", controller)) {
        finishStream("extraQuestions", controller);
        commit("SET_LOADING", false);
        commit("SET_IS_PROCESSING_EXTRA_QUESTIONS", false);
      }
    },
    // Keep only the context questions the user has already answered
    keepAnsweredContextQuestions({ commit, state }) {
      commit(
        "SET_CONTEXT_QUESTION_LIST",
        state.contextQuestionList.filter((contextQuestion) =>
          state.chosenAnswers.some(
            (chosenAnswer) =>
              chosenAnswer.question.question === contextQuestion.question &&
              chosenAnswer.options.length > 0
          )
        )
      );
    },
    clearUnansweredContextQuestions({ commit, dispatch }) {
      stopStream("contextQuestions");
      dispatch("keepAnsweredContextQuestions");
      commit("SET_CONTEXT_QUESTION_LOADING", false);
    },
    // Answer the questions the context or documents already answer
    async prefillFromContext({
      state,
      commit,
      dispatch,
      rootState,
      rootGetters,
    }) {
      await dispatch("waitForFinishLoading");
      const answered = (question) =>
        state.chosenAnswers.some(
          (chosen) =>
            chosen.question.id === question.id && chosen.options.length > 0
        );
      const unanswered = state.questionList.filter(
        (question) => !answered(question)
      );
      const context = rootState.context.context.trim();
      const documents = rootGetters["context/documentsForRequest"];
      const images = rootGetters["context/imagesForRequest"];
      if (
        !unanswered.length ||
        (!context && !documents.length && !images.length)
      ) {
        return;
      }

      const controller = startStream("prefill");
      const { response } = await fetchData("generate/questions/prefill", {
        questions: unanswered.map((question) => question.question),
        context,
        documents,
        images,
      });
      if (!isCurrentStream("prefill", controller)) return;
      finishStream("prefill", controller);
      for (const { question: text, answer } of response.result?.answers || []) {
        const question = state.questionList.find(
          (item) => item.question === text
        );
        if (!question || answered(question)) continue;
        if (!question.suggestedAnswers.includes(answer)) {
          commit("APPEND_QUESTION_ANSWER", { question, value: answer });
        }
        dispatch("toggleSelect", { option: answer, question });
        commit("MARK_FROM_CONTEXT", question);
      }
    },
    async addContextQuestions(
      { dispatch, getters, rootState, rootGetters },
      { replaceQuestions }
    ) {
      if (replaceQuestions) {
        stopStream("contextQuestions");
        dispatch("keepAnsweredContextQuestions");
      }
      const asked = [
        ...JSON.parse(getters.getQuestionListAsString),
        ...JSON.parse(getters.getContextQuestionListAsString),
      ];
      dispatch("context/resetChangedAfterQuestionsGenerated", null, {
        root: true,
      });
      dispatch("prefillFromContext");
      dispatch("getContextQuestions", {
        askedQuestionList: JSON.stringify(asked),
        context: rootState.context.context,
        documents: rootGetters["context/documentsForRequest"],
        images: rootGetters["context/imagesForRequest"],
      });
    },
    async getContextQuestions(
      { dispatch, commit, state, getters },
      {
        askedQuestionList,
        context,
        documents = [],
        images = [],
        retries = 0,
      } = {}
    ) {
      if (retries === 0) {
        await dispatch("waitForFinishLoading");
      }
      const controller = startStream("contextQuestions");
      commit("SET_CONTEXT_QUESTION_LOADING", true);
      commit("SET_ID", Math.max(state.id, getters.getHighestId + 1));
      try {
        const { status, count } = await streamList(
          "generate/questions/context",
          { askedQuestions: askedQuestionList, context, documents, images },
          {
            signal: controller.signal,
            onItem: (item) =>
              dispatch("addQuestion", {
                item,
                location: "APPEND_CONTEXT_QUESTION_LIST",
              }),
          }
        );
        if (status === 401) {
          dispatch("modals/openAuth", null, { root: true });
        } else if (status >= 500 || (status === 200 && count === 0)) {
          throw new StreamError("No context questions were generated");
        }
      } catch (error) {
        if (!isCurrentStream("contextQuestions", controller)) return;
        if (isRetryable(error) && retries < RETRY_LIMIT) {
          setTimeout(() => {
            if (isCurrentStream("contextQuestions", controller)) {
              dispatch("getContextQuestions", {
                askedQuestionList,
                context,
                documents,
                images,
                retries: retries + 1,
              });
            }
          }, retryDelay(retries));
          return;
        }
        console.error(error);
      }
      if (isCurrentStream("contextQuestions", controller)) {
        finishStream("contextQuestions", controller);
        commit("SET_CONTEXT_QUESTION_LOADING", false);
      }
    },
    toggleSelect({ commit, state }, { option, question }) {
      const foundQuestion = state.chosenAnswers.find(
        (item) => item.question.id === question.id
      );
      if (foundQuestion) {
        if (foundQuestion.options.includes(option)) {
          foundQuestion.options = foundQuestion.options.filter(
            (item) => item !== option
          );
        } else {
          foundQuestion.options.push(option);
        }
      } else {
        // if no match found in the chosenAnswers, create a new entry.
        commit("APPEND_CHOSEN_ANSWERS", {
          question: question,
          options: [option],
        });
      }
    },
    addAnswer({ commit, dispatch }, { value, question }) {
      commit("APPEND_QUESTION_ANSWER", { question, value });
      dispatch("toggleSelect", { option: value, question });
    },
    addContextAnswer({ dispatch }, answer) {
      dispatch("addAnswer", answer);
    },
    changeAnswer({ commit, state, dispatch }, { value, question }) {
      if (value === "") return;
      const previous = state.activeEdit;
      commit("SET_QUESTION_ANSWER", { question, value });
      dispatch("toggleSelect", { option: previous, question });
      commit("SET_ACTIVE_EDIT", "");
      dispatch("toggleSelect", { option: value, question });
    },
    changeContextAnswer({ dispatch }, answer) {
      dispatch("changeAnswer", answer);
    },
  },
  getters: {
    // The first set of questions has finished; swapping one out doesn't count
    questionsReady(state) {
      return !state.loading || state.isProcessingExtraQuestions;
    },
    getHighestId(state) {
      return [...state.questionList, ...state.contextQuestionList].reduce(
        (maxId, question) => Math.max(maxId, question.id),
        0
      );
    },
    getQuestionListAsString(state) {
      return JSON.stringify(
        state.questionList.map((question) => ({
          question: question.question,
          suggestedAnswers: question.suggestedAnswers,
        }))
      );
    },
    getContextQuestionListAsString(state) {
      return JSON.stringify(
        state.contextQuestionList.map((question) => ({
          question: question.question,
          suggestedAnswers: question.suggestedAnswers,
        }))
      );
    },
    // chosen options for each question id, for quick lookups in templates
    chosenOptionsById(state) {
      const byId = {};
      state.chosenAnswers.forEach((item) => {
        byId[item.question.id] = item.options;
      });
      return byId;
    },
  },
  modules: {},
};
